import Notification from "@components/common/Notification";
import SelectInput from "@components/form/SelectInput";
import { TextInputDefault } from "@components/form/TextInput";
import { API_URL } from "@redux/api";
import { RootState } from "@redux/store";
import { validateAddInventories } from "@redux/validations/inventories";
import axios, { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { InventoryItemLoading, NotificationType } from "react-app-env";
import { FaTrashAlt } from "react-icons/fa";
import { FiPlus } from "react-icons/fi";
import { useSelector } from "react-redux";

type Props = {
  loadingId: string | undefined;
  inventories: InventoryItemLoading[] | [];
  onCloseModal: () => void;
};

type InventoryItem = {
  item: any;
  quantity: number | undefined;
  unit: string;
  trip: number;
};

type InventoriesStateType = InventoryItem[] | [];

type LineFormError = {
  name?: string | undefined;
  quantity?: string | undefined;
  unit?: string | undefined;
};

type ErrorsState = LineFormError[] | [];

export type AddInventoriesPayload = {
  item: any;
  quantity: number | undefined;
  unit: string;
  trip: number;
};

const LoadingOutConfirmation = ({
  loadingId,
  inventories,
  onCloseModal
}: Props) => {
  const { user } = useSelector((state: RootState) => state.auth);

  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const [inventoriesOptions, setInventoriesOptions] = useState<any>([]);

  const [inventoriesReport, setInventoriesReport] = useState<InventoriesStateType>([]);
  const [errors, setErrors] = useState<ErrorsState>([]);
  const [notification, setNotification] = useState<NotificationType | null>(null);
  
  useEffect(() => {
    const defaultErrors = [];
    const defaultInventories = [];
    
    if(inventories.length === 0) {
      defaultInventories.push({
        item: null,
        quantity: undefined,
        category: null,
        unit: "",
        trip: 0
      });

      defaultErrors.push({
        name: undefined,
        quantity: undefined,
        unit: undefined
      });
    };

    const inventoriesOpts = inventories.map(item => {
      return {
        label: item.item.name,
        value: item._id,
        category: item.item.category._id,
        unit: item.unit
      }
    });
    
    setInventoriesOptions(inventoriesOpts);
    setInventoriesReport(defaultInventories);
    setErrors(defaultErrors);

    return () => {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const onAddNewLineForm = (e: any) => {
    e.preventDefault();
    const inventoriesValue = [...inventoriesReport, {
      item: null,
      quantity: undefined,
      unit: "",
      trip: 0
    }];

    const errorsValue = [...errors, {
      name: undefined,
      quantity: undefined,
      unit: undefined
    }];

    setInventoriesReport(inventoriesValue);
    setErrors(errorsValue);
  };

  const onRemoveLineForm = (e: any, idx: number) => {
    e.preventDefault();
    const newInventories = [...inventoriesReport];
    newInventories.splice(idx, 1);

    const newErrors = [...errors];
    errors.splice(idx, 1);

    setInventoriesReport(newInventories);
    setErrors(newErrors);
  };

  const onChangeError = ({target, value, idx}: {
    target: "name" | "quantity";
    value: string | undefined;
    idx: number;
  }) => {
    const newErrors = errors.map((item, sidx) => {
      if(idx === sidx) {
        return {
          ...item,
          [target]: value
        }
      };

      return item;
    });

    setErrors(newErrors);
  };

  const onChangeInput = (value: any, idx: number, targetName: string) => {
    onChangeError({
      target: targetName === 'item' ? "name" : "quantity",
      value: undefined,
      idx
    });

    const newInventories = inventoriesReport.map((inventory, sidx) => {
      if(idx !== sidx) return inventory;
      if(targetName === 'item') {
        return {
          ...inventory,
          unit: value.unit,
          [targetName]: value
        }
      };

      return {
        ...inventory,
        [targetName]: value
      }
    });

    setInventoriesReport(newInventories);
  };

  const isValid = (payload: AddInventoriesPayload[]) => {
    const { isValid, errors } = validateAddInventories(payload);
    if(errors.length > 0) setErrors(errors);
    return isValid;
  };

  const onSubmit = async(e: any) => {
    e.preventDefault();
    setNotification(null);
    setSubmitLoading(true);

    if(isValid(inventoriesReport)) {
      const deficienciesPayload = inventoriesReport.map(inv => {
        return {
          loadingId,
          adminCreator: user._id,
          inventory: inv.item.value,
          inventoryCategory: inv.item.category,
          description: null,
          quantity: Number(inv.quantity),
          unit: inv.unit
        }
      })

      const payload = {
        deficiencies: deficienciesPayload,
        admin_id: user._id
      };
      
      const response: AxiosResponse = await axios.post(`${API_URL}/loading/${loadingId}/confirmation`, payload);
      const { status, data } = response;

      if(status === 200) {
        setNotification({
          isError: false,
          title: "Sukses!",
          text: "Loading Out sudah terkonfirmasi"
        });
        
        setTimeout(() => {
          setSubmitLoading(false);
          onCloseModal();
        }, 2000);
      } else {
        setNotification({
          isError: false,
          title: "Sukses!",
          text: data.message
        });
        setSubmitLoading(false);
      };
    } else {
      setNotification({
        isError: true,
        title: "Error",
        text: "Mohon lengkapi form di atas dengan benar"
      });
      setSubmitLoading(false);
    };
  };

  return (
    <div>
      <div className="mt-3 py-3">
        <strong>
          Laporan Barang Hilang
        </strong>
      </div>
      <form>
        {inventoriesReport.map((item, idx) => {
          return (
            <div key={`new-inventory-${idx}`} className="mt-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-semibold pb-1">Nama barang</label>
                  <SelectInput
                    isMulti={false}
                    label=""
                    // label={idx === 0 ? 'Nama barang' : ""}
                    placeholder="Pilih barang"
                    disabled={submitLoading}
                    options={inventoriesOptions}
                    value={item.item}
                    isSearchable={true}
                    error={errors[idx]?.name}
                    onChange={(value) => {
                      onChangeInput(value, idx, "item");
                    }}
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-semibold pb-1">Qty {item.unit && `(${item.unit})`}</label>
                  <div className="flex items-center justify-center gap-3">
                    <TextInputDefault 
                      name={`quantity_${idx}`}
                      label=""
                      // label={idx === 0 ? 'Qty' : ""}
                      type="number"
                      value={item.quantity}
                      placeholder="Jumlah"
                      onChange={({ target }) => {
                        const { value } = target;
                        onChangeInput(value, idx, "quantity");
                      }}
                      disabled={submitLoading}
                      error={errors[idx]?.quantity}
                    />
                    <button className="text-red-500" onClick={(e) => onRemoveLineForm(e, idx)}>
                      <FaTrashAlt className="" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        })}

        { inventoriesReport.length === 0 && 
          <div className="my-2 border py-10 rounded-xl">
            <p className="text-center text-sm">Tidak ada barang hilang</p>
          </div>
        }

        <button 
          className="bg-sky-500 text-white rounded-xl py-3 flex items-center justify-center w-full mt-4"
          onClick={onAddNewLineForm}
        >
          <FiPlus />
          Tambah barang hilang
        </button>

        <hr className="mt-5 mb-8" />

        <Notification notification={notification} />

        <button 
          className="bg-orange-600 text-white rounded-xl py-3 flex items-center justify-center w-full mt-4"
          onClick={onSubmit}
        >
          Konfirmasi
        </button>
      </form>
    </div>
  )
}

export default LoadingOutConfirmation